import { BaseMmsMockService } from "services/mms/mock/entities-services/BaseMockService"
import { FetchMockProps } from "types/mms-base"
import { RfidServiceInterface } from "interfaces/rfid-service.ts"
import { CommunicationService } from "services/mms/mock/CommunicationService.ts"
import { RfidSchema } from "mock/schemas"
import { RfidInfo } from "types/rfid"
import { DriverInfo } from "types/list-card-holder"
import { BackendSchema, RuleSchema } from "layouts/charging-rules/interface/Rules.ts"
import { RfidStatus } from "layouts/rfid/utils/const.ts"

export class MockRfidService
    extends BaseMmsMockService
    implements RfidServiceInterface
{
    constructor(communicationService: CommunicationService) {
        super(communicationService)
    }

    private mapRfid = (rfid: RfidSchema): RfidInfo => {
        let status: RfidStatus
        if (rfid.active) {
            status = RfidStatus.Active
        } else if (rfid.toBeActivated) {
            status = RfidStatus.ToBeActivate
        } else {
            status = RfidStatus.Deactivate
        }
        const driver = this.communicationService.drivers.find(
            (d) => d.id === rfid.driver_id
        )

        return {
            id: rfid.id,
            cardHolder: driver?.firstName + " " + driver?.lastName,
            cardHolderId: driver ? `${driver.id}` : "undefined",
            rfidNumber: rfid.number,
            status: status,
            cardHolderType: "DRIVER",
        }
    }

    useGetListRfidCards = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: 500,
            controllerFn: ({user}) => {
                const rfidCardsFromSelfEntity = this.communicationService.rfidCards
                    .map((card) => ({ ...card }))
                    .filter((card) => card.entity_id === user?.entity_id)
                return {
                    data: rfidCardsFromSelfEntity.map(this.mapRfid),
                }
            },
        }
        return this.get(datas)
    }

    useGetListRfidActiveCards = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: 500,
            controllerFn: ({user}) => {
                const rfidCardsFromSelfEntity = this.communicationService.rfidCards
                    .map((card) => ({ ...card }))
                    .filter((card) => card.entity_id === user?.entity_id && card.active).length
                return {
                    data: rfidCardsFromSelfEntity,
                }
            },
        }
        return this.get(datas)
    }

    useGetListRfidUnactiveCards = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: 500,
            controllerFn: ({user}) => {
                const rfidCardsFromSelfEntity = this.communicationService.rfidCards
                    .map((card) => ({ ...card }))
                    .filter((card) => card.entity_id === user?.entity_id && !card.active).length
                return {
                    data: rfidCardsFromSelfEntity,
                }
            },
        }
        return this.get(datas)
    }

    useGetDriverRfidsCards = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ body}) => {
                const { id } = body as DriverInfo
                const rfidCardsFromSelfEntity = this.communicationService.rfidCards
                    .map((card) => ({ ...card }))
                    .filter((card) => card.driver_id === id)
                return  {
                    data: rfidCardsFromSelfEntity.map(this.mapRfid),
                }
            },
        }
        return this.post(datas)
    }

    useUpdateRfidCard = (rfid_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ body, user }) => {
                const rfidCard = this.communicationService.rfidCards.find(
                    (d) => d.id === rfid_id
                )
                if (!rfidCard || rfidCard.entity_id !== user?.entity_id) {
                    return { status: 404, error: "Driver not found" }
                }
                const { id, status} = body as RfidInfo
                rfidCard.driver_id = id
                rfidCard.active = (status == RfidStatus.Active)
                rfidCard.toBeActivated = false
            },
        }
        return this.put(datas)
    }

    useUpdateStatusRfidCard = (rfid_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ body, user }) => {
                const rfidCard = this.communicationService.rfidCards.find(
                    (d) => d.id === rfid_id
                )
                if (!rfidCard || rfidCard.entity_id !== user?.entity_id)
                    return { status: 404, error: "Driver not found" }
                const { description, active } = body as RfidSchema
                rfidCard.active = active
                rfidCard.description = description
            },
        }
        return this.put(datas)
    }

    useGetRfidCardRule = (rfid_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const rfidCard = this.communicationService.rfidCards.find(
                    (d) => d.id === rfid_id
                )
                if (!rfidCard || rfidCard.entity_id !== user?.entity_id)
                    return { status: 404, error: "RFID card not found" }

                // Return a default rule schema for the RFID card
                const payload: BackendSchema = {
                    rules: RuleSchema,
                }
                return { data: payload }
            },
        }
        return this.get(datas)
    }

    useUpdateRfidCardRule = (
        rfid_id: string,
        rfid_rule: BackendSchema
    ): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const rfidCard = this.communicationService.rfidCards.find(
                    (d) => d.id === rfid_id
                )
                if (!rfidCard || rfidCard.entity_id !== user?.entity_id)
                    return { status: 404, error: "RFID card not found" }

                return { data: { id: rfid_id, rules: rfid_rule.rules } }
            },
        }
        return this.post(datas)
    }
}
